import { useState, type KeyboardEvent } from "react";
import { useLantern, useSession } from "../hooks";
import { rank, type Entry } from "../switcher";
import { Avatar } from "./ui";
import { t } from "../i18n";

// To is someone or somewhere to send to: a person, or a channel one is in.
export type To = Entry & { kind: "person" | "channel"; id: string };

// RecipientPicker chooses whom a message goes to: one or more people, for a
// direct message, or one channel one is in.
export function RecipientPicker({ to, onChange, autoFocus }: { to: To[]; onChange: (to: To[]) => void; autoFocus?: boolean }) {
  const session = useSession();
  const state = useLantern();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const me = state.me?.id;
  const channel = to.find((t) => t.kind === "channel");

  const all: To[] = [
    ...[...session.directory.values()]
      .filter((p) => p.id !== me && p.status === "active" && p.kind !== "system")
      .map((p) => ({ key: "p" + p.id, kind: "person" as const, id: p.id, label: p.display_name || p.handle, also: "@" + p.handle, rank: p.kind === "agent" ? 1 : 0 })),
    ...[...state.channels.values()]
      .filter((c) => (c.channel.kind === "public" || c.channel.kind === "private") && !c.channel.archived_at)
      .map((c) => ({ key: "c" + c.channel.id, kind: "channel" as const, id: c.channel.id, label: session.channelName(c.channel.id), also: c.channel.topic, rank: 0 })),
  ];
  // A channel stands alone; people can be several.
  const choices = q.trim() && !channel ? rank(all.filter((x) => !to.some((t) => t.key === x.key) && (to.length === 0 || x.kind === "person")), q, 8) : [];
  const at = Math.min(active, Math.max(choices.length - 1, 0));

  function add(t: To | undefined) {
    if (!t) return;
    onChange([...to, t]);
    setQ("");
    setActive(0);
  }
  function onKey(e: KeyboardEvent) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setActive((at + (e.key === "ArrowDown" ? 1 : -1) + choices.length) % Math.max(choices.length, 1));
    } else if (e.key === "Enter" && choices.length) {
      e.preventDefault();
      add(choices[at]);
    } else if (e.key === "Backspace" && !q && to.length) {
      onChange(to.slice(0, -1));
    }
  }

  return (
    <>
      <div className="compose-to">
        <span className="compose-label">{t("To")}</span>
        {to.map((r) => (
          <span key={r.key} className="chip">
            {r.kind === "person" && <Avatar id={r.id} name={r.label} size={18} />}
            {r.label}
            <button type="button" aria-label={t("Remove {name}", { name: r.label })} onClick={() => onChange(to.filter((x) => x.key !== r.key))}>
              ×
            </button>
          </span>
        ))}
        {!channel && (
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKey}
            placeholder={to.length ? t("Add someone else") : t("#a-channel, @somebody or a name")}
            aria-label={t("To")}
            autoFocus={autoFocus}
            autoComplete="off"
            spellCheck={false}
          />
        )}
      </div>
      {choices.length > 0 && (
        <ul className="rows compose-choices" role="listbox" aria-label={t("Recipients")}>
          {choices.map((c, i) => (
            <li key={c.key} role="option" aria-selected={i === at} className={i === at ? "active" : ""} onMouseMove={() => setActive(i)} onClick={() => add(c)}>
              {c.kind === "person" ? <Avatar id={c.id} name={c.label} size={26} /> : <span className="compose-hash">#</span>}
              <span className="row-main">
                <span className="row-title">{c.label}</span>
                {c.also && <span className="row-meta">{c.also}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// sendTo posts text to where the picker chose, opening the direct message
// first if it is to people, and says the conversation's ID.
export async function sendTo(session: ReturnType<typeof useSession>, to: To[], text: string): Promise<string> {
  const channel = to.find((t) => t.kind === "channel");
  const id = channel ? channel.id : (await session.api.openDM(to.map((t) => t.id))).channel.id;
  if (!channel) await session.loadDMMembers();
  await session.engine.send(id, session.mentionsIn(text), { threadRootID: null, fileIDs: [] });
  return id;
}
